import { Service } from '~/post/types.generated';
import { AttachmentTypes, MediaAttachmentTypes } from '../attachments';
import { createService } from './createService';

export const ServiceOmni = createService('omni', {
  isOmni: true,
  maxAttachableImagesCount: 10,
  canEditLinkAttachment: true,
});

export const ServiceTwitter = createService(Service.Twitter, {
  charLimit: 280,
  unavailableAttachmentTypes: [AttachmentTypes.LINK],
  maxAttachableImagesCount: 4,
  videoMaxSize: 512 * 1024 * 1024, // 512MB
  videoMaxDurationMs: 140 * 1000, // 140s
  shouldShowDuplicateContentWarning: true,
  unavailableMediaAttachmentTypes: [MediaAttachmentTypes.WEBM],
  transcodeVideo: true,
  imageMaxSize: 5 * 1024 * 1024, // 5MB
  gifMaxSize: 15 * 1024 * 1024, // 15MB
});

export const ServiceMastodon = createService(Service.Mastodon, {
  charLimit: 500,
  unavailableAttachmentTypes: [AttachmentTypes.LINK, AttachmentTypes.RETWEET],
  maxAttachableImagesCount: 4,
  videoMaxSize: 40 * 1024 * 1024, // 40MB
  transcodeVideo: false,
  imageMaxSize: 8 * 1024 * 1024, // 8MB
  imageMaxWidth: 10000,
  imageMaxHeight: 10000,
  gifMaxSize: 10 * 1024 * 1024, // 10MB
});

export const ServiceFacebook = createService(Service.Facebook, {
  charLimit: 5000,
  previewCharLimit: 100,
  previewCharLimitWithMedia: 100,
  unavailableAttachmentTypes: [AttachmentTypes.RETWEET],
  maxAttachableImagesCount: 10,
  doesRequireHumanInteractionToUpdateText: true,
  canEditLinkAttachment: true,
  canEditVideoTitle: true,
});

export const ServiceInstagram = createService(Service.Instagram, {
  charLimit: 2200,
  previewCharLimit: 100,
  commentCharLimit: 2200,
  formattedName: 'Instagram',
  unavailableAttachmentTypes: [AttachmentTypes.LINK, AttachmentTypes.RETWEET],
  maxAttachableImagesCount: 10,
  requiredAttachmentType: AttachmentTypes.MEDIA,
  videoMinDurationMs: 3 * 1000, // 3s
  videoMaxDurationMs: 60 * 1000, // 60s
  videoMaxAspectRatio: 16 / 9,
  videoMinAspectRatio: 4 / 5,
  videoMaxAspectRatioHumanReadable: '16:9',
  videoMinAspectRatioHumanReadable: '4:5',
  videoMaxSize: 100 * 1024 * 1024, // 100MB
  videoMaxWidth: 1920,
  videoMinFrameRate: 23,
  videoMaxFrameRate: 60,
  usesImageFirstLayout: true,
  maxHashtags: 30,
  maxMentions: 5,
  canHaveLocation: true,
  canHaveUserTags: true,
  canEditVideoAttachment: true,
  unavailableMediaAttachmentTypes: [MediaAttachmentTypes.WEBM],
  // REELS
  videoMinAspectRatioReels: 1 / 100,
  videoMaxAspectRatioReels: 10 / 1,
  videoMinAspectRatioHumanReadableReels: '0.01:1',
  videoMaxAspectRatioHumanReadableReels: '10:1',
  videoMinDurationMsReels: 3 * 1000, // 5 seconds for Reels
  videoMaxDurationMsReels: 15 * 60 * 1000, // 15 minutes for Reels
  videoMaxSizeReels: 1024 * 1024 * 1024, // 1 GB for Reels
  // STORIES
  videoMinAspectRatioStories: 1 / 100,
  videoMaxAspectRatioStories: 10 / 1,
  videoMinAspectRatioHumanReadableStories: '0.01:1',
  videoMaxAspectRatioHumanReadableStories: '10:1',
  videoMinDurationMsStories: 3 * 1000, // 3 seconds for Stories
  videoMaxDurationMsStories: 60 * 1000, // 60 seconds for Stories
  videoMaxSizeStories: 50 * 1024 * 1024, // 50 MB for Stories
  imageMaxSizeStories: 8 * 1024 * 1024, // 8MB for Stories
});

export const ServiceLinkedIn = createService(Service.Linkedin, {
  charLimit: 3000,
  previewCharLimit: 100,
  formattedName: 'LinkedIn',
  unavailableAttachmentTypes: [AttachmentTypes.RETWEET],
  canEditLinkAttachment: true,
  canEditVideoAttachment: false,
  maxAttachableImagesCount: 9,
  transcodeVideo: false,
});

export const ServiceGoogleBusiness = createService(Service.Googlebusiness, {
  charLimit: 1500,
  previewCharLimit: 220,
  previewTitleCharLimit: 58,
  formattedName: 'Google Business Profile',
  unavailableAttachmentTypes: [AttachmentTypes.RETWEET, AttachmentTypes.LINK],
  canEditVideoAttachment: true,
  usesImageFirstLayout: true,
  videoMaxSize: 100 * 1024 * 1024, // 100MB
  imageMaxSize: 25 * 1024 * 1024, // 25MB
  maxAttachableImagesCount: 1,
  unavailableMediaAttachmentTypes: [
    MediaAttachmentTypes.WEBM,
    MediaAttachmentTypes.M4V,
    MediaAttachmentTypes.GIF,
    MediaAttachmentTypes.MP4,
    MediaAttachmentTypes.VIDEO,
    MediaAttachmentTypes.MOV,
    MediaAttachmentTypes.AVI,
  ],
  attachmentMinSize: 10 * 1024, // 10KB
});

export const ServicePinterest = createService(Service.Pinterest, {
  unavailableAttachmentTypes: [AttachmentTypes.LINK, AttachmentTypes.RETWEET],
  requiredAttachmentType: AttachmentTypes.MEDIA,
  hasSubprofiles: true,
  titleCharLimit: 100,
  charLimit: 500,
  canHaveSourceUrl: true,
  requiresText: false,
  requiresSourceUrl: true,
  usesImageFirstLayout: true,
  canEditVideoAttachment: true,
  unavailableMediaAttachmentTypes: [MediaAttachmentTypes.WEBM],
  transcodeVideo: false,
});

export const ServiceTiktok = createService(Service.Tiktok, {
  unavailableAttachmentTypes: [AttachmentTypes.LINK, AttachmentTypes.RETWEET],
  unavailableMediaAttachmentTypes: [
    MediaAttachmentTypes.JPG,
    MediaAttachmentTypes.JPEG,
    MediaAttachmentTypes.PNG,
    MediaAttachmentTypes.GIF,
    MediaAttachmentTypes.AVI,
    MediaAttachmentTypes.M4V,
    MediaAttachmentTypes.IMAGE,
  ],
  requiredAttachmentType: AttachmentTypes.MEDIA,
  requiresText: false,
  usesImageFirstLayout: true,
  canEditVideoAttachment: false,
  maxAttachableImagesCount: 1,
  videoMaxSize: 1024 * 1024 * 1024, // 1GB
  videoMinResolution: 360,
  charLimit: 150,
  previewCharLimit: 84,
  videoMinDurationMs: 3 * 1000, // 3s
  // 10 minutes and 0.9 seconds because a bit more should be allowed
  // to accommodate for videos slightly longer.
  videoMaxDurationMs: 600_900,
  transcodeVideo: false,
  onlyAllowsVerticalVideo: true,
});

export const ServiceYoutube = createService(Service.Youtube, {
  unavailableAttachmentTypes: [AttachmentTypes.LINK, AttachmentTypes.RETWEET],
  unavailableMediaAttachmentTypes: [
    MediaAttachmentTypes.JPG,
    MediaAttachmentTypes.JPEG,
    MediaAttachmentTypes.PNG,
    MediaAttachmentTypes.GIF,
    MediaAttachmentTypes.M4V,
    MediaAttachmentTypes.IMAGE,
  ],
  requiredAttachmentType: AttachmentTypes.MEDIA,
  requiresText: false,
  usesImageFirstLayout: true,
  canEditVideoAttachment: false,
  maxAttachableImagesCount: 1,
  videoMaxSize: 10 * 1024 * 1024 * 1024, // 10GB
  videoMinResolution: 360,
  charLimit: 5000,
  videoMinDurationMs: 1 * 1000, // 1s
  videoMaxDurationMs: 60 * 1000, // 60s
  transcodeVideo: false,
  onlyAllowsVerticalVideo: true,
  previewTitleCharLimit: 100,
});

export const ServiceStartPage = createService(Service.StartPage, {
  charLimit: 5000,
  previewCharLimit: 100,
  formattedName: 'Start Page',
  unavailableAttachmentTypes: [AttachmentTypes.RETWEET, AttachmentTypes.LINK],
  usesImageFirstLayout: true,
  imageMaxSize: 25 * 1024 * 1024, // 25MB
  maxAttachableImagesCount: 1,
  unavailableMediaAttachmentTypes: [
    MediaAttachmentTypes.WEBM,
    MediaAttachmentTypes.M4V,
    MediaAttachmentTypes.MP4,
    MediaAttachmentTypes.VIDEO,
    MediaAttachmentTypes.MOV,
    MediaAttachmentTypes.AVI,
  ],
  attachmentMinSize: 10 * 1024, // 10KB
});
